import { Component, contentChild, input, output, viewChild } from '@angular/core';
import { MenuTrigger } from '@angular/aria/menu';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';

import { BaseComponent } from '../base/base.component';
import { MenuAction } from './menu-action.types';
import { MenuHeaderDirective } from './menu-header.directive';
import { MenuPanelComponent } from './panel/menu-panel.component';
import { MatRipple } from '@angular/material/core';

/**
 * General dropdown menu built on @angular/aria, driven by a MenuAction[] config.
 *
 * - Trigger content is projected (avatar, icon, text button, …).
 * - Header content is projected via <ng-template immoMenuHeader> and may change per page.
 * - Actions support icons, css classes (e.g. 'danger'), disabled state, separators,
 *   and nesting via `children` (rendered as keyboard-navigable submenus).
 *
 * Usage:
 * ```html
 * <immo-menu [actions]="profileActions" triggerAriaLabel="Profilmenü" (actionSelected)="onAction($event)">
 *     <immo-image [src]="avatarUrl()" [alt]="userName()" fit="cover" />
 *
 *     <ng-template immoMenuHeader>
 *         <span class="name">{{ userName() }}</span>
 *         <span class="email">{{ email() }}</span>
 *     </ng-template>
 * </immo-menu>
 * ```
 */
@Component({
    selector: 'immo-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./theme.css', './menu.component.css'],
    imports: [MenuTrigger, OverlayModule, MenuPanelComponent, MatRipple],
})
export class MenuComponent<T = unknown> extends BaseComponent {
    // -----------------------------------------------------------------------------------------------------
    // @ INPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly actions = input.required<MenuAction<T>[]>();

    /** Accessible label for the trigger; required because triggers are often icon-only. */
    readonly triggerAriaLabel = input.required<string>();

    /** The trigger button's css classes — the 40px icon square by default; hosts like the
     * sidebar footer pass a full-width row so the WHOLE line opens the menu. */
    readonly triggerClass = input<string>('size-[40px]');

    /** Overlay positions for the root panel. Defaults to dropping below, right-aligned. */
    readonly positions = input<ConnectedPosition[]>([
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    ]);

    // -----------------------------------------------------------------------------------------------------
    // @ OUTPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** Emits the value of the activated item, from any nesting level. */
    readonly actionSelected = output<T>();

    // -----------------------------------------------------------------------------------------------------
    // @ CONTENT CHILDREN
    // -----------------------------------------------------------------------------------------------------

    readonly header = contentChild(MenuHeaderDirective);

    // -----------------------------------------------------------------------------------------------------
    // @ VIEW CHILDREN
    // -----------------------------------------------------------------------------------------------------

    /**
     * The root panel; undefined until the overlay is first opened. The trigger's
     * [menu] binding resolves reactively once the panel is instantiated — this is
     * the documented @angular/aria pattern for menus inside overlays.
     */
    readonly panel = viewChild<MenuPanelComponent<T>>(MenuPanelComponent);
}
